import React from 'react'
import { type ThreeElements, useLoader } from '@react-three/fiber'
import { forwardRef, ForwardRefExoticComponent, Fragment, PropsWithoutRef, RefAttributes, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import * as THREE from 'three'
import { CanvasTexture, DoubleSide, Object3D, Vector2, Vector3, ColorRepresentation, Matrix4, Shape, ShapeGeometry } from 'three'
import { SVGLoader, type SVGResult } from 'three-stdlib'
import { Line } from '@react-three/drei'
import { floorplannerStore } from '../../store/floorplannerStore'
import { FillPattern, isSvgType } from '../../types/floorplannerTypes'
import PatternFillMesh from './PatternFillMesh'
import { clampFillOpacity } from '../../utils/fillOpacity'
import {
  acquireSvgFillGeometries,
  buildSvgFillGeometryCacheKey,
  releaseSvgFillGeometries,
} from '../../utils/svgGeometryCache'
import {
  rasterStrokeCss,
  shouldRasterizeSvg,
  SvgRasterLoader,
  toSvgRasterLoaderUrl,
} from '../../utils/svgRasterCache'

type ForwardRefComponent<P, T> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>
type MeshBasicMaterialProps = ThreeElements['meshBasicMaterial']
type MeshProps = ThreeElements['mesh']
type Object3DProps = ThreeElements['object3D']

const isPositiveFiniteNumber = (value: number): boolean => (
  Number.isFinite(value) && value > 0
);

const SVG_RATIO_EPSILON = 1e-4

const syncStoredSvgRatio = (svgId: string, ratio: number) => {
  if (!isPositiveFiniteNumber(ratio)) {
    return
  }
  const symbol = floorplannerStore.symbolsMap.get(svgId)
  if (!isSvgType(symbol)) {
    return
  }
  if (
    typeof symbol.svgRatio === 'number' &&
    Math.abs(symbol.svgRatio - ratio) < SVG_RATIO_EPSILON
  ) {
    return
  }
  floorplannerStore.updateSymbolProperty(svgId, 'svgRatio', ratio)
}

export interface SvgProps extends Omit<Object3DProps, 'ref'> {
  /** src can be a URL or SVG data */
  svgId: string,
  src: string
  lockAspectRatio?: boolean
  skipFill?: boolean
  skipStrokes?: boolean
  fillMaterial?: MeshBasicMaterialProps
  fillOpacity?: number
  fillPattern?: FillPattern
  strokeMaterial?: MeshBasicMaterialProps
  fillMeshProps?: MeshProps
  strokeMeshProps?: MeshProps
  lineWeight?: number
  lineColor?: ColorRepresentation
  width?: number
  height?: number
  lineType?: string
  renderOrder?: number
  flipped?: boolean // Whether the SVG should be flipped (for backward compatibility)
  flipHorizontal?: boolean // Flip on X-axis
  flipVertical?: boolean // Flip on Y-axis
  /** Used to keep symbol-library SVGs vector while rasterizing catalog/AI. */
  libraryKey?: string
  hasProduct3D?: boolean
  hasUserModel3D?: boolean
}

// Inner component that uses useLoader - this can suspend
function SvgContent({
  loaderSource,
  svgId,
  skipFill,
  skipStrokes,
  fillMaterial,
  lineColor,
  lineWeight,
  lineType,
  width,
  height,
  lockAspectRatio,
  renderOrder,
  fillMeshProps,
  noopRaycast,
}: {
  loaderSource: string;
  svgId: string;
  skipFill: boolean;
  skipStrokes: boolean;
  fillMaterial?: MeshBasicMaterialProps;
  lineColor: ColorRepresentation;
  lineWeight: number;
  lineType: string;
  width?: number;
  height?: number;
  lockAspectRatio?: boolean;
  renderOrder: number;
  fillMeshProps?: MeshProps;
  noopRaycast: () => null;
}) {
  // useLoader is called here - if it suspends, only this component suspends
  const svg = useLoader(SVGLoader, loaderSource);
  // Calculate stroke geometries (Vector2 points) for the lines
  const strokeGeometries = useMemo(
    () => {
      if (!svg || !svg.paths) {
        return [];
      }
      if (skipStrokes) {
        return [];
      }
      return svg.paths.map((path: any) => {
        if (path.userData?.style.stroke === undefined || path.userData.style.stroke === 'none') return null;
        const subPathsPoints = path.subPaths.map((subPath: any) => subPath.getPoints());
        return subPathsPoints;
      });
    },
    [svg, skipStrokes]
  );
  
  // Compute the bounding box using the path points
  const { minX, minY, maxX, maxY } = useMemo(() => {
    if (!svg || !svg.paths || svg.paths.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    svg.paths.forEach((path: any) => {
      path.subPaths.forEach((subPath: any) => {
        subPath.getPoints().forEach((point: Vector2) => {
          if (point.x < minX) minX = point.x
          if (point.x > maxX) maxX = point.x
          if (point.y < minY) minY = point.y
          if (point.y > maxY) maxY = point.y
        })
      })
    })
    
    return { minX, minY, maxX, maxY };
  }, [svg])
  
  useEffect(() => {
    if (isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY)) {
      syncStoredSvgRatio(svgId, (maxX - minX) / (maxY - minY))
    }
  }, [minX, minY, maxX, maxY, svgId])

  // **Check if calculations are ready - must be done after all hooks**
  const calculationsReady = isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY);

  // Calculate the actual width and height of the SVG based on the min/max points
  // Use safe defaults when calculations aren't ready
  const actualWidth = calculationsReady ? maxX - minX : 0
  const actualHeight = calculationsReady ? maxY - minY : 0
  const aspectRatio = actualHeight > 0 ? actualWidth / actualHeight : 1
  // Initialize scaleFactorX and scaleFactorY
  // The stored svgLength/svgHeight are what the property panel reports, so the
  // drawing always follows them. Aspect ratio locking is applied where those
  // values are edited; deriving the height from the SVG's own ratio here made
  // the drawn symbol drift a few millimetres away from the reported size.
  const intrinsicHeight = width && aspectRatio > 0 ? width / aspectRatio : actualHeight
  const effectiveHeight = height || (lockAspectRatio ? intrinsicHeight : actualHeight)
  const scaleFactorX = width && actualWidth > 0 ? width / actualWidth : 1
  const scaleFactorY = actualHeight > 0 ? effectiveHeight / actualHeight : 1
  const targetWidth = width || actualWidth
  const targetHeight = effectiveHeight

  // Function to scale the points for lines
  const scalePoints = (points: Vector2[]) => {
    return points.map(point => new Vector2(
      (point.x - minX) * scaleFactorX, // Scale and translate x
      (point.y - minY) * scaleFactorY  // Scale and translate y
    ))
  }

  // Function to scale the shape geometry by creating a new scaled shape
  const scaleShape = (shape: Shape) => {
    const scaledShape = shape.clone()
    const points = scaledShape.getPoints().map(p => {
      return new Vector2(
        (p.x - minX) * scaleFactorX,
        (p.y - minY) * scaleFactorY
      )
    })

    // Create a new shape using the scaled points
    const newShape = new Shape(points)
    return newShape
  }

  // Always return the same structure - conditionally render content inside
  if (!calculationsReady || !svg || !svg.paths || svg.paths.length === 0) {
    return null;
  }

  return (
    <object3D
      position={[-targetWidth / 2, -targetHeight / 2, 0]}
    >
      {svg.paths.map((path: any, p: number) => (
        <Fragment key={`svg${p}`}>
          {!skipFill &&
            path.userData?.style.fill !== undefined &&
            path.userData.style.fill !== 'none' &&
            SVGLoader.createShapes(path).map((shape: Shape, s: number) => (
              <group key={`fill-${p}-${s}`}>
                <mesh
                  {...fillMeshProps}
                  renderOrder={renderOrder}
                  raycast={noopRaycast}
                >
                  <shapeGeometry args={[scaleShape(shape)]} />
                  <meshBasicMaterial
                    color={path.userData!.style.fill}
                    opacity={path.userData!.style.fillOpacity}
                    side={DoubleSide}
                    depthWrite={true}
                    depthTest={true}
                    {...fillMaterial}
                  />
                </mesh>
              </group>
            ))}
          {!skipStrokes &&
            strokeGeometries[p] &&
            path.userData?.style.stroke !== undefined &&
            path.userData.style.stroke !== 'none' &&
            path.subPaths.map((_subPath: any, s: number) => (
              <Line
                key={`stroke-${p}-${s}`}
                points={scalePoints(strokeGeometries[p]![s])} // Use the scaled points for the Line component
                color={lineColor}
                lineWidth={lineWeight}
                opacity={path.userData!.style.strokeOpacity}
                renderOrder={renderOrder + 0.2}
                {...(lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
                depthWrite={true}
                depthTest={true}
              />
            ))}
        </Fragment>
      ))}
    </object3D>
  );
}

const RasterizedSvg: ForwardRefComponent<SvgProps, Object3D> = forwardRef<Object3D, SvgProps>(
  function RasterizedSvg(
    {
      src,
      fillOpacity = 1,
      lineWeight = 1,
      lineColor = 'black',
      width,
      height,
      renderOrder = 0,
      flipped = true,
      flipHorizontal = false,
      flipVertical = false,
      // Gate-only props — must not reach object3D
      libraryKey: _libraryKey,
      hasProduct3D: _hasProduct3D,
      hasUserModel3D: _hasUserModel3D,
      ...props
    },
    ref,
  ) {
    const rasterUrl = toSvgRasterLoaderUrl(src, {
      stroke: rasterStrokeCss(lineColor),
      lineWeight,
    })
    const texture = useLoader(SvgRasterLoader, rasterUrl) as CanvasTexture
    const noopRaycast = () => null
    const image = texture.image as { width?: number; height?: number }
    const aspect =
      image?.width && image?.height && image.height > 0
        ? image.width / image.height
        : 1
    const targetWidth = width && width > 0 ? width : 1
    const targetHeight = height && height > 0 ? height : targetWidth / aspect
    const scaleX = (flipHorizontal ? -1 : 1) * targetWidth
    const scaleY = (flipped ? -1 : 1) * (flipVertical ? -1 : 1) * targetHeight

    return (
      <object3D ref={ref} {...props}>
        <mesh
          scale={[scaleX, scaleY, 1]}
          renderOrder={renderOrder}
          raycast={noopRaycast}
        >
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial
            map={texture}
            transparent
            opacity={clampFillOpacity(fillOpacity)}
            depthWrite
            depthTest
            side={DoubleSide}
          />
        </mesh>
      </object3D>
    )
  },
)

const VectorSvg: ForwardRefComponent<SvgProps, Object3D> = forwardRef<Object3D, SvgProps>(
  function VectorSvg(
    { svgId,
      src,
      lockAspectRatio,
      skipFill = false,
      skipStrokes = false,
      fillMaterial,
      fillOpacity = 1,
      fillPattern,
      strokeMaterial,
      fillMeshProps,
      strokeMeshProps,
      lineWeight = 1,
      lineColor = 'black',
      lineType = 'solid',
      width,
      height,
      renderOrder = 0,
      flipped = true,
      flipHorizontal = false,
      flipVertical = false,
      // Gate-only props — must not reach object3D
      libraryKey: _libraryKey,
      hasProduct3D: _hasProduct3D,
      hasUserModel3D: _hasUserModel3D,
      ...props
    },
    ref
  ) {
    const svg = useLoader(SVGLoader, !src.startsWith('<svg') ? src : `data:image/svg+xml;utf8,${src}`) as SVGResult
    const svgPaths = svg?.paths ?? []
    const noopRaycast = () => null;

    // Calculate stroke geometries (Vector2 points) for the lines
    const rawStrokePoints = useMemo(
      () =>
        skipStrokes
          ? []
          : svgPaths.map((path) => {
            if (path.userData?.style.stroke === undefined || path.userData.style.stroke === 'none') return null;
            const subPathsPoints = path.subPaths
              .map((subPath) => subPath.getPoints())
              .filter((points) => points.length > 1); // Skip degenerate sub-paths to avoid LineGeometry errors
            return subPathsPoints.length > 0 ? subPathsPoints : null;
          }),
      [svgPaths, skipStrokes]
    )
    // Compute the bounding box using the path points
    const { minX, minY, maxX, maxY } = useMemo(() => {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity

      svgPaths.forEach((path) => {
        path.subPaths.forEach((subPath) => {
          subPath.getPoints().forEach((point) => {
            if (point.x < minX) minX = point.x
            if (point.x > maxX) maxX = point.x
            if (point.y < minY) minY = point.y
            if (point.y > maxY) maxY = point.y
          })
        })
      })
      return { minX, minY, maxX, maxY }
    }, [svgPaths])

    useEffect(() => {
      syncStoredSvgRatio(svgId, (maxX - minX) / (maxY - minY))
    }, [svgId, minX, minY, maxX, maxY])

    // **Add this condition to prevent rendering before calculations are complete**
    const calculationsReady = isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY);

    // Calculate the actual width and height of the SVG based on the min/max points
    const actualWidth = calculationsReady ? maxX - minX : 0
    const actualHeight = calculationsReady ? maxY - minY : 0

    let scaleFactorX = 1
    let scaleFactorY = 1

    if (width && actualWidth !== 0) {
      scaleFactorX = width / actualWidth
    }
    // Draw at the stored svgHeight rather than re-deriving it from the SVG's
    // own ratio, so the symbol always measures exactly what the property panel
    // reports. Aspect ratio locking is enforced where the values are edited.
    if (height && actualHeight !== 0) {
      scaleFactorY = height / actualHeight
    } else if (lockAspectRatio) {
      scaleFactorY = scaleFactorX
    }

    if (!isFinite(scaleFactorX) || scaleFactorX === 0) {
      scaleFactorX = 1
    }
    if (!isFinite(scaleFactorY) || scaleFactorY === 0) {
      scaleFactorY = lockAspectRatio ? scaleFactorX : 1
    }

    const targetWidth = actualWidth * scaleFactorX
    const targetHeight = actualHeight * scaleFactorY

    const baseTransform = useMemo(() => {
      const matrix = new Matrix4()
      if (flipped) {
        // Original behavior: don't flip Y coordinate
        matrix.set(
          1, 0, 0, -minX,
          0, 1, 0, -minY,
          0, 0, 1, 0,
          0, 0, 0, 1
        )
      } else {
        // Current behavior: flip Y coordinate
        matrix.set(
          1, 0, 0, -minX,
          0, -1, 0, maxY,
          0, 0, 1, 0,
          0, 0, 0, 1
        )
      }
      
      // Apply user flip transformations
      // After baseTransform, geometry is at [0, 0] with size [actualWidth, actualHeight]
      // To flip around center: flip around actualWidth/2 and actualHeight/2
      if (flipHorizontal) {
        // x' = actualWidth - x (flip around center)
        const flipMat = new Matrix4();
        flipMat.set(
          -1, 0, 0, actualWidth,
           0, 1, 0, 0,
           0, 0, 1, 0,
           0, 0, 0, 1
        );
        matrix.premultiply(flipMat);
      }
      if (flipVertical) {
        // y' = actualHeight - y (flip around center)
        const flipMat = new Matrix4();
        flipMat.set(
          1,  0, 0, 0,
          0, -1, 0, actualHeight,
          0,  0, 1, 0,
          0,  0, 0, 1
        );
        matrix.premultiply(flipMat);
      }
      
      return matrix
    }, [minX, maxX, minY, maxY, flipped, flipHorizontal, flipVertical, actualWidth, actualHeight])

    const fillGeometryCacheKey = useMemo(
      () =>
        buildSvgFillGeometryCacheKey(src, {
          skipFill,
          flipped,
          flipHorizontal,
          flipVertical,
        }),
      [src, skipFill, flipped, flipHorizontal, flipVertical],
    )

    const tessellationInputRef = React.useRef({ svg, baseTransform, skipFill })
    tessellationInputRef.current = { svg, baseTransform, skipFill }

    const [fillGeometries, setFillGeometries] = useState<ShapeGeometry[][]>([])

    /**
     * Acquire/release in layout so Strict Mode cleanup cannot dispose geometry
     * still held by a useMemo result, and identical product SVGs tessellate once.
     */
    useLayoutEffect(() => {
      if (!calculationsReady) {
        setFillGeometries([])
        return
      }

      const geometries = acquireSvgFillGeometries(fillGeometryCacheKey, () => {
        const { svg: cachedSvg, baseTransform: cachedTransform, skipFill: cachedSkipFill } =
          tessellationInputRef.current
        if (cachedSkipFill) return []
        const cachedPaths = cachedSvg?.paths ?? []

        return cachedPaths.map((path) => {
          const baseShapes = SVGLoader.createShapes(path)
          const node = path.userData?.node as SVGPathElement | null | undefined
          const parent = node?.parentElement as SVGElement | null | undefined
          const isStrokeFillPath =
            node?.getAttribute('data-floorplanner-stroke-fill') === '1' ||
            parent?.getAttribute?.('data-floorplanner-stroke-fill-group') === '1'
          const shapes =
            isStrokeFillPath && baseShapes.length === 0 ? path.toShapes(true) : baseShapes

          return shapes.map((shape) => {
            const geometry = new ShapeGeometry(shape.clone())
            geometry.applyMatrix4(cachedTransform)
            return geometry
          })
        })
      })
      setFillGeometries(geometries)

      return () => {
        releaseSvgFillGeometries(fillGeometryCacheKey)
      }
    }, [calculationsReady, fillGeometryCacheKey])

    const normalizedStrokePoints = useMemo(() => {
      if (skipStrokes) return []

      return rawStrokePoints.map((pathPoints) => {
        if (!pathPoints) return null

        return pathPoints.map((points) =>
          points.map((point) => {
            let x = point.x - minX;
            let y;
            
            if (flipped) {
              y = point.y - minY;
            } else {
              // Current behavior: flip Y coordinate (SVG Y is inverted)
              // This matches what baseTransform does: y = maxY - point.y
              y = maxY - point.y;
            }
            
            // Apply flip transformations around the center
            if (flipHorizontal) {
              x = actualWidth - x;
            }
            if (flipVertical) {
              y = actualHeight - y;
            }
            
            return new Vector3(x, y, 0);
          })
        )
      })
    }, [rawStrokePoints, skipStrokes, minX, maxY, minY, flipped, flipHorizontal, flipVertical, actualWidth, actualHeight])

    if (!calculationsReady) {
      return null;
    }

    // Always return the same structure - conditionally render content inside
    // useLoader is called inside SvgContent, so if it suspends, only that component suspends
    // The outer component's hooks above are always called
    return (
      <object3D ref={ref} {...props}>
        <object3D
          position={[-targetWidth / 2, -targetHeight / 2, 0]}
          scale={[scaleFactorX, scaleFactorY, 1]}
        >
          {svgPaths.map((path, p) => {
            const node = path.userData?.node as SVGPathElement | null | undefined;
            const parent = node?.parentElement as (SVGElement | null | undefined);
            const styleFill = path.userData?.style?.fill;
            const attrFill = node?.getAttribute('fill') ?? undefined;
            const overrideFill = (() => {
              const color = fillMaterial?.color;
              if (typeof color === 'string' && color.trim() === '') return undefined;
              return color as ColorRepresentation | undefined;
            })();
            const baseFill = styleFill ?? attrFill;
            const isStrokeFillPath = (node?.getAttribute('data-floorplanner-stroke-fill') === '1') ||
              (parent?.getAttribute?.('data-floorplanner-stroke-fill-group') === '1');
            const hasFill = !skipFill && (isStrokeFillPath || ((overrideFill ?? baseFill) !== undefined && (overrideFill ?? baseFill) !== 'none'));

            const parseOpacity = (value?: number | string | null) => {
              if (value === undefined || value === null) return undefined;
              if (typeof value === 'number') return value;
              const parsed = parseFloat(value);
              return Number.isFinite(parsed) ? parsed : undefined;
            };

            const styleOpacity = path.userData?.style?.fillOpacity;
            const attrOpacity = node?.getAttribute('fill-opacity');
            const effectiveOpacity = parseOpacity(styleOpacity) ?? parseOpacity(attrOpacity) ?? 1;
            const attrStrokeOpacity = node?.getAttribute('stroke-opacity');
            const pathFillGeometries = fillGeometries[p] ?? [];
            const objectOpacity = clampFillOpacity(fillOpacity);
            const intrinsicStrokeOpacity =
              parseOpacity(path.userData?.style?.strokeOpacity) ?? parseOpacity(attrStrokeOpacity) ?? 1;
            // Object fillOpacity must tint strokes too — many furniture SVGs
            // draw frames/legs as strokes and cushions as fills.
            const strokeOpacity = intrinsicStrokeOpacity * objectOpacity;

            return (
              <Fragment key={`svg${p}`}>
                {hasFill &&
                  pathFillGeometries.map((geometry, s) => {
                    const fillColor = overrideFill ?? baseFill ?? lineColor;
                    const intrinsicFillOpacity = isStrokeFillPath ? 1 : effectiveOpacity;
                    const resolvedFillOpacity = intrinsicFillOpacity * objectOpacity;
                    const { color: _ignoredColor, ...restFillMaterial } = fillMaterial ?? {};
                    const materialTransparent = restFillMaterial.transparent ?? resolvedFillOpacity < 1;
                    const depthWrite = restFillMaterial.depthWrite ?? resolvedFillOpacity >= 1;
                    const depthTest = restFillMaterial.depthTest ?? true;

                    // Convert fillColor to number for PatternFillMesh
                    const fillColorNumber = typeof fillColor === 'string'
                      ? new THREE.Color(fillColor).getHex()
                      : (fillColor as number);

                    // Use PatternFillMesh when fillPattern is set
                    if (fillPattern) {
                      return (
                        <mesh
                          key={`fill-${p}-${s}`}
                          {...fillMeshProps}
                          renderOrder={isStrokeFillPath ? renderOrder + 0.1 : renderOrder}
                          geometry={geometry}
                          raycast={noopRaycast}
                        >
                          <PatternFillMesh
                            fillColor={fillColorNumber}
                            fillPattern={fillPattern}
                            useWorldSpace={false}
                            opacity={resolvedFillOpacity}
                            transparent={materialTransparent}
                            side={DoubleSide}
                            depthWrite={depthWrite}
                            depthTest={depthTest}
                          />
                        </mesh>
                      );
                    }

                    return (
                      <mesh
                        key={`fill-${p}-${s}`}
                        {...fillMeshProps}
                        renderOrder={isStrokeFillPath ? renderOrder + 0.1 : renderOrder}
                        geometry={geometry}
                        raycast={noopRaycast}
                      >
                        <meshBasicMaterial
                          {...restFillMaterial}
                          color={fillColor}
                          opacity={resolvedFillOpacity}
                          transparent={materialTransparent}
                          side={DoubleSide}
                          depthWrite={depthWrite}
                          depthTest={depthTest}
                        />
                      </mesh>
                    )
                  })}
              {!skipStrokes &&
                normalizedStrokePoints[p] &&
                path.userData?.style.stroke !== undefined &&
                path.userData.style.stroke !== 'none' &&
                normalizedStrokePoints[p]!.map((points, s) => (
                  <Line
                    key={`stroke-${p}-${s}-${Math.round(strokeOpacity * 100)}`}
                    points={points}
                    color={lineColor}
                    lineWidth={lineWeight}
                    opacity={strokeOpacity}
                    transparent={strokeOpacity < 1}
                    renderOrder={renderOrder + 0.2}
                    {...(lineType === "dashed" && {
                      dashed: true,
                      dashSize: 0.15,
                      gapSize: 0.1,
                    })}
                    raycast={noopRaycast}
                    depthWrite={strokeOpacity >= 1}
                    depthTest
                  />
                ))}
            </Fragment>
)})}
          </object3D>
      </object3D>
    )
  }
)

/**
 * `useLoader` throws during render when a source cannot be fetched (stale
 * /static/media hash, CORS, malformed markup). Without a boundary that error
 * escapes the Canvas and takes down the whole plan, so each render strategy
 * degrades instead: raster falls back to vector, vector falls back to nothing.
 */
class SvgLoadErrorBoundary extends React.Component<
  {
    /** Retry when the symbol points at a different source. */
    resetKey: string
    fallback: React.ReactNode
    children: React.ReactNode
    onError?: (error: unknown) => void
  },
  { failed: boolean; failedKey: string | null }
> {
  state: { failed: boolean; failedKey: string | null } = {
    failed: false,
    failedKey: null,
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    this.setState({ failed: true, failedKey: this.props.resetKey })
    this.props.onError?.(error)
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false, failedKey: null })
    }
  }

  render() {
    if (
      this.state.failed &&
      (!this.state.failedKey || this.state.failedKey === this.props.resetKey)
    ) {
      return this.props.fallback
    }
    return this.props.children
  }
}

const warnedSvgSources = new Set<string>()

const warnOnceForSource = (message: string, src: string, error: unknown) => {
  const key = `${message}:${src}`
  if (warnedSvgSources.has(key)) {
    return
  }
  warnedSvgSources.add(key)
  console.warn(message, { src, error })
}

export const Svg: ForwardRefComponent<SvgProps, Object3D> = forwardRef<Object3D, SvgProps>(
  function Svg(props, ref) {
    const vector = <VectorSvg ref={ref} {...props} />

    // Vector is the last resort, so a dead source renders nothing at all.
    const guardedVector = (
      <SvgLoadErrorBoundary
        resetKey={props.src}
        fallback={null}
        onError={(error) =>
          warnOnceForSource('Svg: could not load source, skipping symbol', props.src, error)
        }
      >
        {vector}
      </SvgLoadErrorBoundary>
    )

    if (!shouldRasterizeSvg(props)) {
      return guardedVector
    }

    return (
      <SvgLoadErrorBoundary
        resetKey={props.src}
        fallback={guardedVector}
        onError={(error) =>
          warnOnceForSource('Svg: raster bake failed, falling back to vector', props.src, error)
        }
      >
        <RasterizedSvg ref={ref} {...props} />
      </SvgLoadErrorBoundary>
    )
  },
)
